/**
 * What happens to a payoff's Amount Due / Amount Paid when the servicer changes what the payoff is
 * actually collecting, and what a waived interest shortfall does to the allocation.
 *
 * Kept out of the modal so the two rules can be read and tested on their own. Both exist because a
 * payoff is negotiated: the quote is a starting point, and the servicer settles for less interest,
 * fewer fees, or charges priced to the date the demand was quoted through.
 */

/**
 * Whether a change in what the payoff is collecting should move Amount Paid along with Amount Due.
 *
 * On a fresh quote it should: Amount Paid is the cash the quote says to collect, so waiving a fee
 * means the borrower sends that much less. Once the servicer has keyed the cash that actually
 * arrived, it must not: the wire is a fact, and moving it asks them to retype the one figure they
 * are certain of. A payoff that is over or short of its own Amount Due is how that shows - a fresh
 * quote is in balance, so both are 0 until one of them is edited.
 *
 * Only a payoff negotiates this way. Every other payment type collects a scheduled amount due, and
 * a change to what is owed moves both figures exactly as it always has.
 */
export const amountPaidFollowsOwed = (isPayoff: boolean, shortfall: number, overage: number): boolean =>
  !isPayoff || (!(shortfall > 0) && !(overage > 0))

/**
 * How much of an uncollected-interest shortfall the servicer has waived.
 *
 * A waiver is a string (possibly empty - the reason is optional), null means carry it. Interest on
 * a loan that capitalizes its shortfall is added to the balance rather than carried as a
 * receivable, so there is nothing there to waive and the waiver reads as 0 however it was set.
 */
export const waivedInterest = (shortfall: number, reason: string | null, capitalizes: boolean): number =>
  reason !== null && !capitalizes && shortfall > 0 ? shortfall : 0
